import { LeaveAreaCode } from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { LeaveColleagueMatrix } from '@/products/leave/features/leave-colleague-matrix';
import {
  berlinTodayIso,
  monthLabel,
  paddedMonthSpan,
} from '@/products/leave/features/leave-calendar-utils';
import { GERMAN_STATE_LABEL } from '@/products/leave/features/leave-labels';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import {
  useLeaveCalendar,
  useLeaveColleagueCalendar,
  useLeaveColleagues,
} from '@/products/leave/features/use-leave';

type Mode = 'team' | 'admin';

export function LeaveTeamCalendarPage() {
  return <ColleagueCalendar mode="team" />;
}

export function LeaveAdminColleagueCalendarPage() {
  return <ColleagueCalendar mode="admin" />;
}

function ColleagueCalendar({ mode }: { mode: Mode }) {
  const copy = useVoiceCopy();
  const me = useMe();
  const areas = useLeaveAreas();
  const today = berlinTodayIso();
  const [year, setYear] = useState(() => Number(today.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(today.slice(5, 7)));
  const span = paddedMonthSpan(year, month);
  const team = mode === 'team';
  const colleagues = useLeaveColleagues(team);
  const departmentCalendar = useLeaveCalendar(team ? span.from : '', team ? span.to : '');
  const adminCalendar = useLeaveColleagueCalendar(span.from, span.to, !team);
  const title = team ? 'Kalenderansicht' : 'Kalenderansicht (Verwaltung)';
  usePageTitle(title);

  function shiftMonth(delta: number) {
    const date = new Date(Date.UTC(year, month - 1 + delta, 1));
    setYear(date.getUTCFullYear());
    setMonth(date.getUTCMonth() + 1);
  }

  const active = team ? departmentCalendar : adminCalendar;
  const people = team ? (colleagues.data?.items ?? []) : (adminCalendar.data?.people ?? []);
  const days = active.data?.days ?? [];
  const stateCode = active.data?.stateCode ?? null;
  const schoolOn = leaveAreaEnabled(areas.data, LeaveAreaCode.SCHOOL_HOLIDAYS);
  const failed = team ? colleagues.isError || departmentCalendar.isError : adminCalendar.isError;
  const error = team
    ? colleagues.error instanceof ApiRequestError
      ? colleagues.error
      : departmentCalendar.error
    : adminCalendar.error;
  const pending = team
    ? colleagues.isPending || departmentCalendar.isPending
    : adminCalendar.isPending;

  return (
    <div className="space-y-6">
      <PlatformPageHeader title={copy('leave.colleagues.title')} description={title} />
      <div className={cn(leavePanelClassName, 'flex flex-wrap items-center gap-2')}>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Vorheriger Monat"
          onClick={() => shiftMonth(-1)}
        >
          <ChevronLeftIcon className="size-4" />
        </Button>
        <h2 className="min-w-40 text-center text-lg font-semibold">{monthLabel(year, month)}</h2>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Nächster Monat"
          onClick={() => shiftMonth(1)}
        >
          <ChevronRightIcon className="size-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setYear(Number(today.slice(0, 4)));
            setMonth(Number(today.slice(5, 7)));
          }}
        >
          Heute
        </Button>
      </div>
      {failed ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiRequestError
            ? error.message
            : 'Der Kalender konnte nicht geladen werden.'}
        </p>
      ) : (
        <>
          {pending ? (
            <p className="text-sm text-muted-foreground">Kalender wird geladen …</p>
          ) : null}
          {schoolOn && stateCode ? (
            <p className="text-sm text-muted-foreground">
              Schulferien: Deutschland – {GERMAN_STATE_LABEL[stateCode]}
            </p>
          ) : null}
          {active.data && people.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {team
                ? copy('leave.calendar.department-empty')
                : 'Im gewählten Monat hat niemand einen Antrag gestellt.'}
            </p>
          ) : null}
          {people.length > 0 && days.length > 0 ? (
            <LeaveColleagueMatrix
              people={people}
              days={days}
              schoolHolidays={active.data?.schoolHolidays ?? []}
              today={today}
              viewerId={me.data?.id ?? ''}
              showDepartment={!team}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
